// The screens this app has, generated from .appfactory/product-plan.json.
//
// Imported by the navigation shell rather than replacing it: the shell owns
// router.js — bottom-tabs, stack and sidebar produce different routers, and it
// rewrites that file on every `spec apply`. Two owners for one file is a silent
// fight, so the product contributes ROUTES and the shell decides what to do
// with them.
//
// `primary` marks a screen that earns a place in the navigation itself.

import RampPage from './pages/RampPage.vue';
import LiftsPage from './pages/LiftsPage.vue';
import TimelinePage from './pages/TimelinePage.vue';
import BarPage from './pages/BarPage.vue';
import KitPage from './pages/KitPage.vue';
import AttemptsPage from './pages/AttemptsPage.vue';
import SchemePage from './pages/SchemePage.vue';

export const PRODUCT_ROUTES = [
  {
    id: 'ramp',
    path: '/lifts/:id/ramp',
    label: 'Ramp',
    component: RampPage,
    primary: true,
    destination: false,
    settings: false,
  },
  {
    id: 'lifts',
    path: '/',
    label: 'Lifts',
    component: LiftsPage,
    primary: false,
    destination: true,
    settings: false,
  },
  {
    id: 'timeline',
    path: '/timeline',
    label: 'Timeline',
    component: TimelinePage,
    primary: false,
    destination: true,
    settings: false,
  },
  {
    id: 'bar',
    path: '/bar',
    label: 'Bar',
    component: BarPage,
    primary: false,
    destination: true,
    settings: false,
  },
  {
    id: 'kit',
    path: '/kit',
    label: 'Kit',
    component: KitPage,
    primary: false,
    // A tab, not a toolbar button. Kit is where a lifter recolours plates and
    // sets bar, collars and units — visited often enough to earn a place in
    // the bar, and four destinations is well inside the five a bar can hold.
    // Still `settings`, so QA and the store screenshots find it by role.
    destination: true,
    settings: true,
  },
  {
    id: 'attempts',
    path: '/lifts/:id/attempts',
    label: 'Film',
    component: AttemptsPage,
    primary: false,
    destination: false,
    settings: false,
  },
  {
    id: 'scheme',
    path: '/lifts/:id/scheme',
    label: 'Warm-up scheme',
    component: SchemePage,
    primary: false,
    destination: false,
    settings: false,
  },
];

export const PRIMARY_ROUTES = PRODUCT_ROUTES.filter((r) => r.primary);

// What the navigation offers. NOT the same question as `primary`.
//
// The tab bar was built from PRIMARY_ROUTES, and `primary` means "the screen
// the app opens on" — exactly one screen per plan. So a four-screen app got a
// one-tab tab bar and three screens nothing could reach. `roles` grades
// importance for QA; it was never a claim about navigation.
//
// A destination is a top-level screen someone navigates TO: primary or
// critical, never a detail screen (reached from its list) and never settings
// (which gets its own toolbar button, because a settings tab crowds out a real
// one). Capped at five — past that a bottom bar stops being usable and the
// platform guidelines say so.
export const DESTINATION_ROUTES = PRODUCT_ROUTES.filter((r) => r.destination).slice(0, 5);

// The settings screen, whatever this app decided to CALL it.
export const SETTINGS_ROUTE = PRODUCT_ROUTES.find((r) => r.settings) ?? null;

// WHERE THE APP OPENS, and it must be a path that can actually resolve.
//
// This took the first `primary` route whatever its shape. A plan is free to
// mark a parameterised screen primary — one did: `capture` at
// `/recipe/:recipeId/capture` — and the app then opened on a path with a
// literal `:` in it, which vue-router cannot match and which becomes
// "Missing required param" at boot.
//
// A destination is already defined as top-level and non-detail, so it is the
// right fallback: prefer a primary screen that can resolve, then any
// destination, then anything without a parameter.
const RESOLVABLE = (r) => !/:[A-Za-z]/.test(r.path);
export const HOME_ROUTE = (
  PRIMARY_ROUTES.find(RESOLVABLE)
  ?? DESTINATION_ROUTES.find(RESOLVABLE)
  ?? PRODUCT_ROUTES.find(RESOLVABLE)
)?.path ?? '/';
